/**
 * 所属しているテナントの一覧と切り替え。
 *
 * ## 全テナントの一覧ではない
 *
 * ここに並ぶのは `viewer.memberships`、つまり**このアカウントが入れる会社**である。
 * visumo が預かっている全社の一覧は「会社の比較」（`operator.tenantSummaries`）
 * のほうで、こちらは切り替えの導線に徹する。
 * 「全部が見えている」と読めてしまう形を作らないよう、画面にそう書く。
 *
 * ## 切り替えはトークンを取り直す
 *
 * tenant_id は引数ではなくトークンのクレームから決まるので、別の会社を見るには
 * その会社向けのトークンを取り直すしかない。顧客向けの会社切り替えと同じ導線である。
 */

import {
  Button,
  Card,
  PageHeader,
  Tag,
  useSession,
} from "visumo-data-agent-web-shared";

import { HelpIcon } from "../help/index";
import { useViewer } from "../ViewerContext";

const ROLE_LABEL: Record<string, string> = {
  TENANT_ADMIN: "管理者",
  TENANT_EDITOR: "編集者",
  TENANT_VIEWER: "閲覧者",
  CS_DELEGATE: "代行",
};

export function TenantsPage() {
  const viewer = useViewer();
  const session = useSession();

  return (
    <div className="vd-page">
      <PageHeader
        title="会社"
        description="いまログインしているアカウントが入れる会社の一覧です。"
        titleExtra={<HelpIcon slug="tenants/list" />}
      />
      <div className="vd-stack">
        <div className="vd-notice" role="status">
          <strong>預かっている全社の一覧ではありません。</strong>
          ここに出るのは、このアカウントが所属している会社だけです。
          会社をまたいで数字を並べるのは「会社の比較」です。
        </div>
        {viewer.memberships.length === 0 ? (
          <Card title="入れる会社がありません">
            <p style={{ margin: 0 }}>
              このアカウントはまだどの会社にも所属していません。
            </p>
          </Card>
        ) : (
          viewer.memberships.map((membership) => {
            const current = membership.tenant.id === viewer.tenant.id;
            return (
              <Card
                key={membership.tenant.id}
                title={
                  <>
                    {membership.tenant.label}
                    {current ? <Tag tone="ok">いま開いています</Tag> : null}
                    <Tag>{ROLE_LABEL[membership.role] ?? membership.role}</Tag>
                  </>
                }
                extra={
                  current ? null : (
                    <Button
                      onClick={() => {
                        void session.login({
                          tenantId: membership.tenant.id,
                          returnTo: "/",
                        });
                      }}
                    >
                      この会社に切り替える
                    </Button>
                  )
                }
              >
                <p className="vd-stat-note" style={{ margin: 0 }}>
                  会社を切り替えるときは、その会社のトークンを取り直すために
                  ログインをもう一度通ります。
                </p>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}
